/**
 * Offline Referral Service
 * 
 * Handles referral operations in offline mode using cached data
 * and IndexedDB storage, bypassing direct Supabase calls
 */

import { indexedDBService } from '@/services/offline/indexeddb';
import { referralCodeOps, referralOps, referralStatsOps } from '@/services/offline/indexeddb/operations';
import { getCurrentDomain } from '@/utils/domainUtils';

export interface ReferralStats {
  totalReferrals: string;
  successfulSignups: string;
  premiumConversions: string;
  rewardsEarned: string;
  progressToNextReward: number;
  recentReferrals: Array<{
    date: string;
    email: string;
    status: string;
  }>;
}

/**
 * Offline Referral Service Class
 */
export class OfflineReferralService {
  /**
   * Check cached permissions for authentication
   * Note: Since the user must be authenticated to access the referral page,
   * we can assume authentication if they have a valid userId
   */
  private async checkPermissions(userId: string): Promise<{ isAuthenticated: boolean; userId: string | null }> {
    // If we have a userId, the user is authenticated (they wouldn't be on this page otherwise)
    return {
      isAuthenticated: !!userId,
      userId: userId || null
    };
  }

  /**
   * Get user's referral code from cache
   */
  async getUserReferralCode(userId: string): Promise<{ code: string | null; url: string | null }> {
    const { isAuthenticated } = await this.checkPermissions(userId);
    if (!isAuthenticated) {
      throw new Error('Authentication required to access referral code');
    }

    await indexedDBService.initialize();
    const referralCode = await referralCodeOps.getByUserId(userId);

    if (referralCode) {
      const url = `${getCurrentDomain(true)}/?ref=${referralCode.code}`;
      return { code: referralCode.code, url };
    }

    return { code: null, url: null };
  }

  /**
   * Get referral statistics from cache
   */
  async getReferralStats(userId: string): Promise<ReferralStats> {
    const { isAuthenticated } = await this.checkPermissions(userId);
    if (!isAuthenticated) {
      throw new Error('Authentication required to access referral statistics');
    }

    await indexedDBService.initialize();
    
    // Try to get cached stats first
    const cachedStats = await referralStatsOps.getByUserId(userId);
    if (cachedStats) {
      return {
        totalReferrals: cachedStats.total_referrals,
        successfulSignups: cachedStats.successful_signups,
        premiumConversions: cachedStats.premium_conversions,
        rewardsEarned: cachedStats.rewards_earned,
        progressToNextReward: cachedStats.progress_to_next_reward,
        recentReferrals: cachedStats.recent_referrals.map(r => ({
          date: r.created_at,
          email: 'Anonymous User', // In offline mode, we don't have email data
          status: r.status
        }))
      };
    }

    // If no cached stats, calculate from referral data
    const referrals = await referralOps.getByReferrerId(userId);
    const totalReferrals = referrals.length;
    const successfulSignups = referrals.filter(r => r.status === 'signed_up' || r.status === 'converted').length;
    const premiumConversions = referrals.filter(r => r.status === 'converted').length;
    
    // Get recent referrals (last 5)
    const recentReferrals = referrals
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 5)
      .map(r => ({
        date: r.created_at,
        email: 'Anonymous User', // In offline mode, we don't have email data
        status: r.status
      }));

    const stats: ReferralStats = {
      totalReferrals: totalReferrals.toString(),
      successfulSignups: successfulSignups.toString(),
      premiumConversions: premiumConversions.toString(),
      rewardsEarned: '0', // Would need additional data to calculate
      progressToNextReward: 0, // Would need additional data to calculate
      recentReferrals
    };

    // Cache the calculated stats for 1 hour
    await referralStatsOps.cacheStats(userId, {
      total_referrals: stats.totalReferrals,
      successful_signups: stats.successfulSignups,
      premium_conversions: stats.premiumConversions,
      rewards_earned: stats.rewardsEarned,
      progress_to_next_reward: stats.progressToNextReward,
      recent_referrals: stats.recentReferrals.map(r => ({
        id: crypto.randomUUID(), // Generate a temporary ID
        created_at: r.date,
        status: r.status
      }))
    }, 60 * 60 * 1000);

    return stats;
  }

  /**
   * Check if referral data is available offline
   */
  async isReferralDataAvailable(userId: string): Promise<boolean> {
    try {
      await indexedDBService.initialize();
      const referralCode = await referralCodeOps.getByUserId(userId);
      return referralCode !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get offline status message
   */
  getOfflineStatusMessage(): string {
    return 'Referral data loaded from offline cache. Connect to internet for real-time updates.';
  }

  /**
   * Clear cached referral data
   */
  async clearCachedData(userId: string): Promise<void> {
    await indexedDBService.initialize();
    
    // Clear referral code
    const referralCode = await referralCodeOps.getByUserId(userId);
    if (referralCode) {
      await referralCodeOps.delete(referralCode.id);
    }

    // Clear referrals
    const referrals = await referralOps.getByReferrerId(userId);
    for (const referral of referrals) {
      await referralOps.delete(referral.id);
    }

    // Clear stats
    await referralStatsOps.clearExpiredStats();
  }
}

// Export singleton instance
export const offlineReferralService = new OfflineReferralService();
